import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Award, 
  Plus, 
  X, 
  Upload, 
  FileText,
  Save,
  Briefcase,
  Languages,
  MapPin,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const ProProfileTab = ({ user, token, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({
    bio: "",
    certifications: [],
    specialties: [],
    languages: [],
    availability: "looking",
    location_preference: "",
    cv_url: "",
    student_count: "",
    number_of_dives: "",
    currently_in: "",
    is_visible: true
  });
  
  const [newCert, setNewCert] = useState({ name: "", level: "", date: "", customName: "", customLevel: "" });
  const [newSpecialty, setNewSpecialty] = useState("");
  const [customSpecialty, setCustomSpecialty] = useState("");
  const [newLanguage, setNewLanguage] = useState("");
  const [customLanguage, setCustomLanguage] = useState("");

  useEffect(() => {
    if (user?.pro_profile) {
      setProfile({
        bio: user.pro_profile.bio || "",
        certifications: user.pro_profile.certifications || [],
        specialties: user.pro_profile.specialties || [],
        languages: user.pro_profile.languages || [],
        availability: user.pro_profile.availability || "looking",
        location_preference: user.pro_profile.location_preference || "",
        cv_url: user.pro_profile.cv_url || "",
        student_count: user.pro_profile.student_count || "",
        number_of_dives: user.pro_profile.number_of_dives || "",
        currently_in: user.currently_in || "",
        is_visible: user.pro_profile.is_visible !== false
      });
    } else if (user) {
      // Pro hasn't created their profile yet — still surface their `currently_in`
      setProfile(p => ({ ...p, currently_in: user.currently_in || "" }));
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // `currently_in` lives on the top-level user record, not inside pro_profile.
      // Split the update into two calls so neither value is silently dropped.
      const { currently_in, cv_url, ...proProfileBody } = profile;
      const calls = [
        axios.put(`${API}/users/me/pro-profile`, proProfileBody, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ];
      if ((currently_in || "") !== (user?.currently_in || "")) {
        calls.push(
          axios.put(`${API}/users/me`, { currently_in: currently_in || null }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
      }
      await Promise.all(calls);
      toast.success("Pro profile updated!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Only PDF files are allowed");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/upload/cv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setProfile({ ...profile, cv_url: res.data.cv_url });
      toast.success("CV uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CV");
    } finally {
      setUploading(false);
    }
  };

  const addCertification = () => {
    const certName = newCert.name === "Other" ? newCert.customName : newCert.name;
    const certLevel = newCert.level === "Other" ? newCert.customLevel : newCert.level;
    
    if (!certName || !certLevel) {
      toast.error("Please enter certification agency and level");
      return;
    }
    setProfile({
      ...profile,
      certifications: [...profile.certifications, { name: certName, level: certLevel, date: newCert.date }]
    });
    setNewCert({ name: "", level: "", date: "", customName: "", customLevel: "" });
  };

  const removeCertification = (index) => {
    setProfile({
      ...profile,
      certifications: profile.certifications.filter((_, i) => i !== index)
    });
  };

  const addSpecialty = () => {
    const specialty = newSpecialty === "Other" ? customSpecialty.trim() : newSpecialty.trim();
    if (!specialty) return;
    if (profile.specialties.includes(specialty)) {
      toast.error("Specialty already added");
      return;
    }
    setProfile({
      ...profile,
      specialties: [...profile.specialties, specialty]
    });
    setNewSpecialty("");
    setCustomSpecialty("");
  };

  const removeSpecialty = (specialty) => {
    setProfile({
      ...profile,
      specialties: profile.specialties.filter(s => s !== specialty)
    });
  };

  const addLanguage = () => {
    const language = newLanguage === "Other" ? customLanguage.trim() : newLanguage.trim();
    if (!language) return;
    if (profile.languages.includes(language)) {
      toast.error("Language already added");
      return;
    }
    setProfile({
      ...profile,
      languages: [...profile.languages, language]
    });
    setNewLanguage("");
    setCustomLanguage("");
  };

  const removeLanguage = (language) => {
    setProfile({
      ...profile,
      languages: profile.languages.filter(l => l !== language)
    });
  };

  const certificationOptions = ["PADI", "SSI", "NAUI", "CMAS", "BSAC", "SDI/TDI", "Other"];
  const levelOptions = ["Open Water Instructor", "Divemaster", "Master Instructor", "Course Director", "Staff Instructor", "IDC Staff", "Other"];
  const availabilityOptions = [
    { value: "looking", label: "Actively Looking" },
    { value: "employed", label: "Currently Employed" },
    { value: "not_looking", label: "Not Looking for Work" }
  ];
  const specialtyPresets = ["Technical Diving", "Cave Diving", "Wreck Diving", "Underwater Photography", "Freediving", "Night Diving", "Deep Diving", "Nitrox", "Rebreather", "Ice Diving", "Other"];
  const languagePresets = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Chinese", "Japanese", "Arabic", "Russian", "Greek", "Other"];

  return (
    <Card className="border-cyan-200">
      <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50">
        <CardTitle className="flex items-center gap-2 text-cyan-700">
          <Award className="w-5 h-5" />
          Pro Profile
        </CardTitle>
        <p className="text-sm text-gray-600">
          Your professional profile is visible to dive centers looking to hire
        </p>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Visibility Option */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={profile.is_visible}
              onChange={(e) => setProfile({ ...profile, is_visible: e.target.checked })}
              className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">
                Show my profile in the Pros Directory
              </span>
              {!profile.is_visible && (
                <p className="text-xs text-amber-600 italic mt-0.5">
                  Your profile is currently hidden from the public directory
                </p>
              )}
            </div>
          </label>
        </div>

        {/* Currently In - Visible to everyone */}
        <div>
          <Label htmlFor="currently_in" className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            Currently In
          </Label>
          <p className="text-xs text-gray-500 mb-1">Where are you located right now? This is visible to everyone.</p>
          <Input
            id="currently_in"
            value={profile.currently_in || ''}
            onChange={(e) => setProfile({ ...profile, currently_in: e.target.value })}
            placeholder="e.g., Bali, Indonesia"
            className="mt-1"
          />
        </div>

        {/* Bio */}
        <div>
          <Label htmlFor="bio">Professional Bio</Label>
          <Textarea
            id="bio"
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            placeholder="Tell dive centers about your experience and passion for diving..."
            rows={4}
            className="mt-1"
          />
        </div>

        {/* Certifications */}
        <div>
          <Label className="flex items-center gap-1">
            <Award className="w-4 h-4" />
            Certifications
          </Label>
          
          {/* Existing Certifications */}
          <div className="mt-2 space-y-2">
            {profile.certifications.map((cert, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <span className="flex-1 text-sm">
                  <strong>{cert.name}</strong> - {cert.level}
                  {cert.date && <span className="text-gray-500 ml-2">({cert.date})</span>}
                </span>
                <button
                  onClick={() => removeCertification(idx)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add New Certification */}
          <div className="mt-3 p-3 border rounded-lg bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <select
                  value={newCert.name}
                  onChange={(e) => setNewCert({ ...newCert, name: e.target.value, customName: "" })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Agency</option>
                  {certificationOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {newCert.name === "Other" && (
                  <Input
                    value={newCert.customName}
                    onChange={(e) => setNewCert({ ...newCert, customName: e.target.value })}
                    placeholder="Enter agency name"
                    className="mt-2"
                  />
                )}
              </div>
              <div>
                <select
                  value={newCert.level}
                  onChange={(e) => setNewCert({ ...newCert, level: e.target.value, customLevel: "" })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Level</option>
                  {levelOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {newCert.level === "Other" && (
                  <Input
                    value={newCert.customLevel}
                    onChange={(e) => setNewCert({ ...newCert, customLevel: e.target.value })}
                    placeholder="Enter level"
                    className="mt-2"
                  />
                )}
              </div>
              <Input
                type="text"
                value={newCert.date}
                onChange={(e) => setNewCert({ ...newCert, date: e.target.value })}
                placeholder="Date (e.g., 2020)"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCertification}
              className="mt-2"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Certification
            </Button>
          </div>
        </div>

        {/* Specialties */}
        <div>
          <Label className="flex items-center gap-1">
            <Briefcase className="w-4 h-4" />
            Specialties
          </Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {profile.specialties.map((specialty) => (
              <span
                key={specialty}
                className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm"
              >
                {specialty}
                <button onClick={() => removeSpecialty(specialty)} className="hover:text-cyan-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex gap-2">
              <select
                value={newSpecialty}
                onChange={(e) => {
                  setNewSpecialty(e.target.value);
                  if (e.target.value !== "Other") setCustomSpecialty("");
                }}
                className="flex-1 p-2 border rounded"
              >
                <option value="">Select or type specialty</option>
                {specialtyPresets.filter(s => !profile.specialties.includes(s) || s === "Other").map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <Button type="button" variant="outline" size="sm" onClick={addSpecialty}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {newSpecialty === "Other" && (
              <Input
                value={customSpecialty}
                onChange={(e) => setCustomSpecialty(e.target.value)}
                placeholder="Enter custom specialty"
                className="mt-1"
              />
            )}
          </div>
        </div>

        {/* Languages */}
        <div>
          <Label className="flex items-center gap-1">
            <Languages className="w-4 h-4" />
            Languages Spoken
          </Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {profile.languages.map((language) => (
              <span
                key={language}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
              >
                {language}
                <button onClick={() => removeLanguage(language)} className="hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex gap-2">
              <select
                value={newLanguage}
                onChange={(e) => {
                  setNewLanguage(e.target.value);
                  if (e.target.value !== "Other") setCustomLanguage("");
                }}
                className="flex-1 p-2 border rounded"
              >
                <option value="">Select language</option>
                {languagePresets.filter(l => !profile.languages.includes(l) || l === "Other").map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <Button type="button" variant="outline" size="sm" onClick={addLanguage}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {newLanguage === "Other" && (
              <Input
                value={customLanguage}
                onChange={(e) => setCustomLanguage(e.target.value)}
                placeholder="Enter custom language"
                className="mt-1"
              />
            )}
          </div>
        </div>

        {/* CV Upload */}
        <div>
          <Label className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            CV / Resume (PDF)
          </Label>
          <p className="text-xs text-gray-500 mb-2">Only visible to dive centers and admins. Max 5MB.</p>
          
          {profile.cv_url && (
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg mb-2">
              <FileText className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">CV uploaded</span>
              <a
                href={`${BACKEND_URL}${profile.cv_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-cyan-600 hover:underline ml-auto"
              >
                View CV
              </a>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept=".pdf"
              onChange={handleCVUpload}
              disabled={uploading}
              className="flex-1"
            />
            {uploading && <span className="text-sm text-gray-500">Uploading...</span>}
          </div>
        </div>

        {/* Visible Only to Dive Centers Section */}
        <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 mb-4">
            <EyeOff className="w-5 h-5 text-amber-700" />
            <h3 className="font-medium text-amber-800">Visible Only to Dive Centers</h3>
          </div>
          <p className="text-xs text-amber-700 mb-4">The following information is only visible to dive centers and admins looking to hire.</p>
          
          {/* Availability */}
          <div className="mb-4">
            <Label>Availability Status</Label>
            <select
              value={profile.availability}
              onChange={(e) => setProfile({ ...profile, availability: e.target.value })}
              className="w-full mt-1 p-2 border rounded-lg"
            >
              {availabilityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Location Preference */}
          <div className="mb-4">
            <Label htmlFor="location">
              <MapPin className="w-4 h-4 inline mr-1" />
              Preferred Work Location
            </Label>
            <Input
              id="location"
              value={profile.location_preference}
              onChange={(e) => setProfile({ ...profile, location_preference: e.target.value })}
              placeholder="e.g., Southeast Asia, Caribbean, Mediterranean"
              className="mt-1"
            />
          </div>

          {/* Experience Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="student_count">Students Certified</Label>
              <Input
                id="student_count"
                value={profile.student_count}
                onChange={(e) => setProfile({ ...profile, student_count: e.target.value })}
                placeholder="e.g., 150+"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="number_of_dives">Number of Dives</Label>
              <Input
                id="number_of_dives"
                value={profile.number_of_dives}
                onChange={(e) => setProfile({ ...profile, number_of_dives: e.target.value })}
                placeholder="e.g., 2000+"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={loading}
          className="w-full bg-cyan-600 hover:bg-cyan-700"
        >
          {loading ? "Saving..." : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Pro Profile
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProProfileTab;
